import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import capitalize from 'capitalize';
import {
    DateField,
    ChipField,
    TextField,
    ReferenceField,
    useUpdate,
    useNotify,
} from 'react-admin';

import Money from '../../components/Money';
import UserReferenceField from '../../components/UserReferenceField';
import CountryField from '../../components/CountryField';

import {
    Table,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Button,
    TextField as MuiTextField,
    IconButton,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { FunctionField } from 'react-admin';
import { formatMoney } from '../../util/formatMoney';

const styles = {
    chip: {
        margin: 4,
    },
    wrapper: {
        display: 'flex',
        flexWrap: 'wrap',
    },
};

const renderValue = value => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
};

const renderMetadataRow = (key, value) => {
    if (typeof value === 'object' && value !== null) {
        return Object.entries(value).map(([subKey, subValue]) => (
            <TableRow key={`${key}-${subKey}`}>
                <TableCell>
                    <label>{`${key}.${subKey}`}</label>
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                    {renderValue(subValue)}
                </TableCell>
            </TableRow>
        ));
    }

    return (
        <TableRow key={key}>
            <TableCell>
                <label>{key}</label>
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
                {renderValue(value)}
            </TableCell>
        </TableRow>
    );
};

const ChargeSummary = props => {
    const { record } = props;
    const notify = useNotify();

    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notesValue, setNotesValue] = useState(record?.notes || '');

    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [descriptionValue, setDescriptionValue] = useState(
        record?.description || ''
    );

    // Check if user is admin
    const userPermissions = JSON.parse(
        localStorage.getItem('permissions') || '[]'
    );
    const isAdmin = userPermissions.includes('admin');

    // useUpdate hook to handle notes updates
    const [updateNotes, { loading: notesLoading }] = useUpdate(
        'charges',
        record?.id,
        { notes: notesValue },
        record,
        {
            undoable: false,
            onSuccess: () => {
                notify('Notes updated successfully', 'info');
                setIsEditingNotes(false);
            },
            onFailure: () => {
                notify('Error updating notes', 'warning');
            },
        }
    );

    // useUpdate hook to handle description updates
    const [updateDescription, { loading: descriptionLoading }] = useUpdate(
        'charges',
        record?.id,
        { description: descriptionValue },
        record,
        {
            undoable: false,
            onSuccess: () => {
                notify('Description updated successfully', 'info');
                setIsEditingDescription(false);
            },
            onFailure: () => {
                notify('Error updating description', 'warning');
            },
        }
    );

    const handleSaveNotes = () => {
        updateNotes();
    };

    const handleCancelNotes = () => {
        setNotesValue(record?.notes || '');
        setIsEditingNotes(false);
    };

    const handleSaveDescription = () => {
        updateDescription();
    };

    const handleCancelDescription = () => {
        setDescriptionValue(record?.description || '');
        setIsEditingDescription(false);
    };

    return (
        <div>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell>Partner</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <ReferenceField
                                label="Partner"
                                record={record}
                                source="partnerId"
                                reference="partners"
                                basePath="/partners"
                                link="show"
                            >
                                <TextField source="name" />
                            </ReferenceField>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Charge Id</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.id}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Invoice Id</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.invoiceId && (
                                <ReferenceField
                                    {...props}
                                    source="invoiceId"
                                    reference="invoices"
                                    link="show"
                                >
                                    <TextField source="id" />
                                </ReferenceField>
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Created At</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <DateField
                                locales="en-IN"
                                record={record}
                                source="createdAt"
                                showTime
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Customer</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <UserReferenceField {...props} label="Customer" />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Type</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <ChipField record={record} source="type" />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Amount</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.type === 'credit' ? (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    -
                                    {formatMoney(
                                        record.amount,
                                        record.currency
                                    )}
                                </>
                            ) : (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    {formatMoney(
                                        record.amount,
                                        record.currency
                                    )}
                                </>
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Description</label>
                        </TableCell>
                        <TableCell
                            style={{
                                textAlign: 'right',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {isAdmin ? (
                                isEditingDescription ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            width: '100%',
                                        }}
                                    >
                                        <MuiTextField
                                            value={descriptionValue}
                                            onChange={e =>
                                                setDescriptionValue(
                                                    e.target.value
                                                )
                                            }
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            rows={2}
                                            style={{ flex: 1 }}
                                            disabled={descriptionLoading}
                                        />
                                        <div>
                                            <IconButton
                                                onClick={handleSaveDescription}
                                                color="primary"
                                                size="small"
                                                disabled={descriptionLoading}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={
                                                    handleCancelDescription
                                                }
                                                color="secondary"
                                                size="small"
                                                disabled={descriptionLoading}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <span>
                                            {record?.description || '-'}
                                        </span>
                                        <IconButton
                                            onClick={() => {
                                                setDescriptionValue(
                                                    record?.description || ''
                                                );
                                                setIsEditingDescription(true);
                                            }}
                                            size="small"
                                            color="primary"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                )
                            ) : (
                                <span>{record?.description || '-'}</span>
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Notes</label>
                        </TableCell>
                        <TableCell
                            style={{
                                textAlign: 'right',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {isEditingNotes ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        width: '100%',
                                    }}
                                >
                                    <MuiTextField
                                        value={notesValue}
                                        onChange={e =>
                                            setNotesValue(e.target.value)
                                        }
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={2}
                                        style={{ flex: 1 }}
                                        disabled={notesLoading}
                                    />
                                    <div>
                                        <IconButton
                                            onClick={handleSaveNotes}
                                            color="primary"
                                            size="small"
                                            disabled={notesLoading}
                                        >
                                            <SaveIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={handleCancelNotes}
                                            color="secondary"
                                            size="small"
                                            disabled={notesLoading}
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <span>{record?.notes || '-'}</span>
                                    <IconButton
                                        onClick={() => {
                                            setNotesValue(record?.notes || '');
                                            setIsEditingNotes(true);
                                        }}
                                        size="small"
                                        color="primary"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            )}
                        </TableCell>
                    </TableRow>
                    {record.startDate &&
                    record.endDate &&
                    record.startDate === record.endDate ? (
                        <TableRow>
                            <TableCell>
                                <label>Date</label>
                            </TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                                <span>
                                    {`${record.startDate}/${record.month}/${record.year}`}
                                </span>
                            </TableCell>
                        </TableRow>
                    ) : (
                        <TableRow>
                            <TableCell>
                                <label>Period</label>
                            </TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                                <span>
                                    {`${record.startDate}/${record.month}/${record.year} to ${record.endDate}/${record.month}/${record.year}`}
                                </span>
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell>
                            <label>Country</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <CountryField record={record} />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            {record.metadata && (
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2}>
                                <strong>Metadata</strong>
                            </TableCell>
                        </TableRow>
                        {Object.entries(record.metadata).map(([key, value]) =>
                            renderMetadataRow(key, value)
                        )}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

ChargeSummary.propTypes = {
    record: PropTypes.object,
};

export default ChargeSummary;
